import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { AppButton, AppCard, IconBadge, WhatsAppButton } from '@/components/atoms';
import { formatUsd } from '@/utils/vehicleFormat';

import { REGIONAL_WHATSAPP_MESSAGE, routes as copy } from '../content';
import { type DeliveryRoute } from '../hooks/useDeliveryRoutes';

import { RoutePriceRow } from './RoutePriceRow';

type Props = {
  route: DeliveryRoute;
  /** The date the figures are effective from, so the panel can be dated. */
  effectiveFrom: string;
  id: string;
  labelledBy: string;
};

/**
 * What delivery to the chosen destination costs.
 *
 * The "from" figure is set in display type because it is the number the
 * visitor came to this page for, and the five weight bands sit under it
 * because the figure on its own invites the next question. The old band put
 * three sentences in three tiles and sent every visitor to the calculator to
 * find out whether any of it was affordable.
 *
 * The panel is dated and says the price covers transport only. Both are there
 * for the same reason: a figure quoted without its scope gets quoted back at
 * us as the whole import.
 */
export function RoutePricePanel({ route, effectiveFrom, id, labelledBy }: Props) {
  return (
    <AppCard
      id={id}
      role="tabpanel"
      aria-labelledby={labelledBy}
      sx={{ borderRadius: 3, p: { xs: 5, md: 7 } }}
    >
      <Stack spacing={5}>
        <Stack direction="row" spacing={{ xs: 4, md: 5 }} alignItems="flex-start">
          <IconBadge name={route.icon} size={48} shape="circle" tone="tint" />
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h3">
              {route.destination}
            </Typography>
            {route.note && (
              <Typography color="text.secondary" sx={{ maxWidth: '52ch' }}>
                {route.note}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Divider />

        {route.pricing === 'published' && route.fromUsd !== null ? (
          <>
            <Stack spacing={2}>
              <Typography variant="overline" color="text.secondary">
                {copy.fromLabel}
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: 'h1.fontFamily',
                  fontWeight: 800,
                  fontSize: { xs: '2.75rem', md: '3.5rem' },
                  lineHeight: 1,
                  color: 'primary.main',
                }}
              >
                {formatUsd(route.fromUsd)}
                <Box component="span" style={visuallyHidden}>
                  {' '}
                  for transport from Mombasa to {route.destination}
                </Box>
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {copy.bandsLabel}
              </Typography>
              <Box component="dl" sx={{ m: 0 }}>
                {route.bands.map((band, index) => (
                  <RoutePriceRow
                    key={band.label}
                    band={band}
                    last={index === route.bands.length - 1}
                  />
                ))}
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={4}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '44ch' }}>
                Road transport only — URA taxes, registration and plates are separate. Figures
                effective from {effectiveFrom}.
              </Typography>
              <AppButton to="/landed-cost-calculator" appVariant="secondary" sx={{ flexShrink: 0 }}>
                {copy.cta}
              </AppButton>
            </Stack>
          </>
        ) : (
          <Stack
            spacing={5}
            sx={{
              p: { xs: 4, sm: 5 },
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h5" component="h4">
                Planning a handover here?
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: '58ch' }}>
                Share your vehicle and final destination with Kabex. The team can discuss the options
                for your order and provide a destination-specific quotation.
              </Typography>
            </Stack>
            <WhatsAppButton message={REGIONAL_WHATSAPP_MESSAGE}>
              Ask about this destination
            </WhatsAppButton>
          </Stack>
        )}
      </Stack>
    </AppCard>
  );
}
